import { useEffect, useState } from 'react';
import { FailedActions } from './FailedActions';
import type { Task } from '../lib/types';
import { launch, navPreference, openNavigation, type NavOption } from '../lib/navigate';
import { currentOrder } from '../lib/currentOrder';
import { codLabel } from '../lib/money';
import { AlarmClock, Banknote, Map as MapIcon, MapPin, MessageCircle, Navigation, Package, Phone } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

/**
 * 当前任务卡片。
 *
 * 信息层级严格按司机的决策顺序排,不是按数据库字段顺序:
 *   收件人 → 地址 → 电话 → 时效 → 件数 → 备注 → 单号
 * 见 docs/14-driver-ux-spec.md §14.3
 */
export function CurrentTask({
  task,
  index,
  total,
  onComplete,
  onChanged,
  onOpenDetail,
  whatsapp = false,
}: {
  task: Task;
  index: number;
  total: number;
  onComplete: () => void;
  onChanged: () => Promise<void>;
  /** 进详情 —— 和列表、地图进的是同一个 */
  onOpenDetail: (orderId: string) => void;
  /** 电话旁边显不显示 WhatsApp 按钮(租户开关) */
  whatsapp?: boolean;
}) {
  const [navChoices, setNavChoices] = useState<NavOption[] | null>(null);
  /*
   * ★ 取送信息和区域默认收起。不记忆展开状态 ——
   *   `task.id` 变了(换了一单)就回到收起,否则某次好奇点开之后
   *   它会一直占着大字地址正下方那块地方。
   */
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => setMoreOpen(false), [task.id]);
  const goingToPickup = task.target === 'PICKUP';

  const address = goingToPickup ? task.pickup.address : task.receiverAddress;
  const title = goingToPickup ? task.pickup.name : task.receiverName;

  /*
   * ★ 电话要跟着**当前要去的那一方**走。
   *
   *   原来不管哪个阶段,📞 拨的都是收件人 —— 而司机在取货阶段
   *   要找的是店里的人:货在哪、谁交接、门锁了怎么办。
   *   打给收件人问不出任何一条。
   */
  const phone = goingToPickup ? task.pickup.contactPhone : task.receiverPhone;

  /** 另一方 —— 顺路显示,免得他为了看一眼地址还要点进详情 */
  const other = goingToPickup
    ? { role: '送到', name: task.receiverName, address: task.receiverAddress }
    : { role: '取自', name: task.pickup.name, address: task.pickup.address };

  function navigate() {
    if (task.lat == null || task.lng == null) return;
    // 点导航 = 宣布「我现在去这一单」(见 currentOrder 里的说明)
    currentOrder.set(task.id);
    const opts = openNavigation(task.lat, task.lng, title);
    if (opts) setNavChoices(opts);
  }

  return (
    <>
      <div className="mx-4 mt-4">
        {/*
          ★ 说清楚这块是什么。
            原来只写「当前 · 第 1/1 单」—— 用户问「这个区域是干什么的」,
            那就是没说清:它是**你现在要去做的那一单**,
            下面那个大按钮对的就是它。不写这一句的话,
            它看起来只是列表里被随机挑出来的一条。
        */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-500">
            现在去做这一单 · 第 {index}/{total}
          </span>
          <span className="text-xs text-slate-400 tabular-nums">{task.orderNo}</span>
        </div>

        {/*
          ★ 点这张卡就进详情,不再单独给一个按钮。
            用户的原话:「点击订单就可以直接查看订单详情吧,不需要有个单独的按钮」——
            对的,一张写着一单信息的卡片,点它的自然结果就是打开那一单。

          ★ 卡上那几个各自可点的东西(电话、WhatsApp、导航、完成本单)
            都要 `stopPropagation` —— 否则点电话会先把详情打开,
            而他要的是打电话。这是「整卡可点」唯一的代价,
            漏一个就会变成很难说清的怪现象。

          ★ 用 div + role/tabIndex 而不是 <button> 包住:
            按钮里套按钮和链接是无效的 HTML,读屏会读不出里面那些。
            键盘用 Enter / 空格触发,和按钮一致。
        */}
        <div
          role="button"
          tabIndex={0}
          aria-label={`查看这一单的详情:${task.receiverName} ${task.orderNo}`}
          onClick={() => onOpenDetail(task.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpenDetail(task.id);
            }
          }}
          className="cursor-pointer rounded-2xl border border-slate-200 dark:border-slate-800
                        bg-white dark:bg-slate-800/50 p-4 shadow-sm">

          {/* 姓名 + 电话:到门口喊的第一句话,和找不到时要打的号 */}
          <div className="flex items-start justify-between gap-3">
            {/*
              ★ 名字用红色。
                这一屏上「现在去哪」是地址,而**到了之后要找谁**是名字 ——
                原来它和周围的深灰字一个颜色,司机在门口那几秒要重新扫一遍
                才能挑出它。红色在这一屏是空的(代收那条只在有钱时出现),
                拿来标这一样不会和别的抢。
                去取货时这里是取货点名称,同样是「到了找谁」。
            */}
            <h2 className="text-xl font-semibold leading-tight text-red-600 dark:text-red-400">
              {title}
            </h2>
            <div className="flex gap-2 shrink-0">
              {phone && (
              <a
                href={`tel:${phone}`}
                onClick={(e) => e.stopPropagation()}
                className="grid place-items-center w-11 h-11 rounded-full bg-green-50
                           dark:bg-green-950 text-green-600 dark:text-green-400 text-xl"
                aria-label="拨打电话"
              >
                <Phone className="size-5" />
              </a>
              )}
              {phone && whatsapp && (
              <a
                href={`https://wa.me/${phone.replace(/\D/g, '')}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="grid place-items-center w-11 h-11 rounded-full bg-slate-100
                           dark:bg-slate-700 text-xl"
                aria-label="WhatsApp"
              >
                <MessageCircle className="size-5" />
              </a>
              )}
            </div>
          </div>

          {/* 地址 18px —— 阳光下要看得清 */}
          <p className="mt-2 text-lg text-slate-700 dark:text-slate-300 leading-snug">
            <InlineIcon icon={MapPin} /> {address}
          </p>

          {/*
            ★ 另一方也露一行。
              大字那块是「现在要去哪」—— 开车的时候只该有一个答案,
              所以它不能变成两段一样大的东西。但「这一单从哪来 / 到哪去」
              是他随手要看的,不该逼他点进详情。
          */}
          {/*
            ★ 「另一方」和「区域」默认收起来。
              这两样都是**参考信息**,不是这一刻要做的事:
              去送货的路上,取货点在哪已经没有意义了(货在车上);
              区域是调度用来分单的词,司机照着导航走,不照着区域走。
              而它们占掉的是这一屏最贵的地方 —— 大字地址正下方,
              司机开车时扫一眼只该看到「去哪、找谁、几件」。

              收起来不是删掉:一行就能展开,而且展开状态**不记忆** ——
              下一单又是默认收起,否则某次好奇点开之后它会永远占着。
          */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMoreOpen((v) => !v);
            }}
            aria-expanded={moreOpen}
            aria-label={moreOpen ? '收起取送信息' : '展开取送信息'}
            className="mt-1 text-sm text-slate-400"
          >
            {/* ★ 折叠条上不写具体地址,只写这里藏着什么 —— 写了就等于没折叠 */}
            取送信息 {moreOpen ? '▲' : '▼'}
          </button>
          {moreOpen && (
            <p className="mt-1 text-sm text-slate-500">
              {other.role} {other.name} · {other.address}
              {task.zone && <span className="ml-2" title="区域"><InlineIcon icon={MapIcon} /> {task.zone.name}</span>}
            </p>
          )}

          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-sm text-slate-600 dark:text-slate-400">
            <span><InlineIcon icon={Package} /> {task.packageCount} 件{task.weightGram ? ` · ${(task.weightGram / 1000).toFixed(1)}kg` : ''}</span>
            {/* ★ 代收改成红的 —— 它和「区域」不是一个量级的事:
                   钱没收到,货就要不回来了 */}
            {codLabel(task.codAmount, task.codPending) && (
              <span className="font-medium text-red-600 dark:text-red-400">
                <InlineIcon icon={Banknote} /> 代收 {codLabel(task.codAmount, task.codPending)}
              </span>
            )}
            {(task.specialItems ?? []).map((it) => (
              <span key={it.code} className="font-medium text-amber-700 dark:text-amber-400">
                {it.icon ? `${it.icon} ` : '⚠ '}
                {it.label}
              </span>
            ))}
          </div>

          {task.expectedDeliveryAt && <Deadline at={task.expectedDeliveryAt} />}

          {/* 客户备注直接影响操作,不能藏 */}
          {(task.receiverNote || task.remark) && (
            <p className="mt-3 rounded-lg bg-amber-50 dark:bg-amber-950/40 px-3 py-2
                          text-sm text-amber-800 dark:text-amber-200">
              <InlineIcon icon={MessageCircle} /> {task.receiverNote || task.remark}
            </p>
          )}

          {task.failedAttempts > 0 && (
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              ⚠ 已尝试投递 {task.failedAttempts} 次
            </p>
          )}
        </div>

        {/* 主 CTA 固定在下方,单手拇指可及 */}
        <div className="mt-3 space-y-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate();
            }}
            disabled={task.lat == null}
            className="w-full h-14 rounded-xl bg-brand-600 text-white text-lg font-medium
                       disabled:opacity-40 active:bg-brand-700 transition"
          >
            <InlineIcon icon={Navigation} /> 导航{task.lat == null ? '(缺少坐标)' : ''}
          </button>

          {/* ACCEPTED 时取货动作在取货点卡片里发起(通常一次取多单),
              这里只在配送阶段显示完成按钮 */}
          {(task.status === 'PICKED_UP' || task.status === 'IN_TRANSIT') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onComplete();
              }}
              className="w-full h-14 rounded-xl border-2 border-slate-300 dark:border-slate-600
                         text-lg font-medium text-slate-900 dark:text-slate-100 active:bg-slate-100
                         dark:active:bg-slate-700 transition"
            >
              完成本单
            </button>
          )}

          {/* ★ FAILED 走单独的两个动作(重投 / 退回)。
                原来这里也复用「完成本单」的面板,而 DELIVER 不接受 FAILED ——
                司机选「已送达」只会拿到一句「请刷新后重试」,而刷新没有用 */}
          {task.status === 'FAILED' && <FailedActions task={task} onDone={onChanged} />}
        </div>
      </div>

      {navChoices && (
        <NavPicker
          options={navChoices}
          onPick={(opt, remember) => {
            if (remember) navPreference.set(opt.key);
            setNavChoices(null);
            launch(opt);
          }}
          onClose={() => setNavChoices(null)}
        />
      )}
    </>
  );
}

/** 时效倒计时。剩余不足 30 分钟标红 */
function Deadline({ at }: { at: string }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const remainMs = new Date(at).getTime() - now;
  const overdue = remainMs < 0;
  const urgent = remainMs < 30 * 60_000;
  const mins = Math.abs(Math.round(remainMs / 60_000));
  const text =
    mins >= 60 ? `${Math.floor(mins / 60)} 小时 ${mins % 60} 分` : `${mins} 分钟`;

  return (
    <p
      className={`mt-2 text-sm font-medium ${
        overdue
          ? 'text-red-600 dark:text-red-400'
          : urgent
            ? 'text-amber-600 dark:text-amber-400'
            : 'text-slate-500'
      }`}
    >
      <InlineIcon icon={AlarmClock} /> {new Date(at).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })} 前送达
      {overdue ? `(已超时 ${text})` : `(剩 ${text})`}
    </p>
  );
}

/** 导航 App 选择。带「记住选择」—— 一天几十次,不能每次都弹 */
function NavPicker({
  options,
  onPick,
  onClose,
}: {
  options: NavOption[];
  onPick: (opt: NavOption, remember: boolean) => void;
  onClose: () => void;
}) {
  const [remember, setRemember] = useState(true);
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
      <div
        className="w-full rounded-t-2xl bg-white dark:bg-slate-800 p-4 pb-safe"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-slate-300 dark:bg-slate-600" />
        <h3 className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
          选择导航应用
        </h3>
        <div className="space-y-2">
          {options.map((o) => (
            <button
              key={o.key}
              onClick={() => onPick(o, remember)}
              className="w-full h-14 rounded-xl border border-slate-200 dark:border-slate-700
                         text-left px-4 text-lg text-slate-900 dark:text-slate-100
                         active:bg-slate-100 dark:active:bg-slate-700"
            >
              {o.name}
            </button>
          ))}
        </div>
        <label className="mt-4 flex items-center gap-2 text-slate-600 dark:text-slate-400">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="w-5 h-5"
          />
          记住我的选择
        </label>
      </div>
    </div>
  );
}
